<template>
  <div class="patient-call-log">
    <div class="filter-grid mb-3">
      <DateRangeFilter v-model="dateRangeFilter" label="Call Date:" />
    </div>

    <div v-if="loading" class="text-center p-3">
      <i class="fa fa-spinner fa-spin"></i>
      Loading call history...
    </div>

    <div v-else-if="error" class="text-center text-danger p-3">
      <i class="fa fa-exclamation-triangle"></i>
      Error loading call history: {{ error }}
    </div>

    <div v-else-if="!calls.length" class="text-center text-muted p-3">
      <i class="fa fa-info-circle"></i>
      No calls found for the selected date range.
    </div>

    <div v-else>
      <b-table class="call-log-table"
               show-empty sort-icon-left
               empty-text="No Calls Found"
               :fields="tableFields"
               :items="calls"
               :sort-by.sync="sortBy"
               :sort-desc.sync="sortDesc"
               small>

        <template #cell(agent)="data">
          {{ formatAgent(data.value) }}
        </template>

        <template #cell(initiated)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(outbound)="data">
          {{ data.value ? 'Outbound' : 'Inbound' }}
        </template>
      </b-table>

      <div class="call-log-stacked">
        <div v-for="(call, idx) in calls" :key="idx" class="call-card">
          <div class="call-card-row">
            <span class="call-card-label">Number:</span>
            <span>{{ call.externalNumber }}</span>
          </div>
          <div class="call-card-row">
            <span class="call-card-label">Agent:</span>
            <span>{{ formatAgent(call.agent) }}</span>
          </div>
          <div class="call-card-row">
            <span class="call-card-label">Initiated:</span>
            <span>{{ formatDate(call.initiated) }}</span>
          </div>
          <div class="call-card-row">
            <span class="call-card-label">Direction:</span>
            <span>{{ call.outbound ? 'Outbound' : 'Inbound' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import { Call } from '@/models/Call';
import DateRangeFilter from '@/components/DateRangeFilter.vue';
import Axios from 'axios';
import moment from 'moment';

@Component({
  name: 'PatientCallLogComponent',
  components: {
    DateRangeFilter
  }
})
export default class PatientCallLogComponent extends Vue {
  @Prop({ required: true }) patient!: Patient;

  protected dateRangeFilter: { startDate: string; endDate: string } = {
    startDate: '',
    endDate: ''
  };

  protected calls: Call[] = [];
  protected loading = false;
  protected error = '';
  protected sortBy = 'initiated';
  protected sortDesc = true;

  protected tableFields = [
    { key: 'externalNumber', label: 'Number', sortable: true },
    { key: 'agent', label: 'Agent', sortable: true, tdClass: 'call-log-agent-cell' },
    { key: 'initiated', label: 'Initiated', sortable: true },
    { key: 'outbound', label: 'Direction', sortable: true }
  ];

  mounted() {
    // Default to last 30 days
    const today = new Date().toDateString();
    const startDate = moment().subtract(30, 'days').toDate().toDateString();
    this.dateRangeFilter = { startDate, endDate: today };
    // Initial load
    this.loadCalls();
  }

  @Watch('dateRangeFilter', { deep: true })
  onDateRangeChanged() {
    this.loadCalls();
  }

  @Watch('patient.id')
  onPatientChanged() {
    if (this.patient?.id) {
      this.loadCalls();
    }
  }

  async loadCalls() {
    if (!this.dateRangeFilter.startDate || !this.dateRangeFilter.endDate) {
      console.log('PatientCallLog: No date range set');
      this.calls = [];
      return;
    }

    if (!this.patient?.id) {
      console.log('PatientCallLog: No patient ID');
      this.calls = [];
      return;
    }

    this.loading = true;
    this.error = '';

    const params: any = {
      startDate: moment(this.dateRangeFilter.startDate).startOf('day').toISOString(),
      endDate: moment(this.dateRangeFilter.endDate).endOf('day').toISOString(),
      patientId: this.patient.id
    };

    console.log('PatientCallLog: Loading calls with params:', params);

    try {
      const response = await Axios.get('/api/Call', { params });
      console.log('PatientCallLog: Response:', response.data);
      this.calls = response.data;
    } catch (err: any) {
      this.error = err.response?.data?.message || err.message || 'An error occurred';
      console.error('PatientCallLog: Error loading call history:', err);
      console.error('PatientCallLog: Error response:', err.response);
    } finally {
      this.loading = false;
    }
  }

  formatDate(date: Date): string {
    return moment.utc(date).local().format('L LT');
  }

  formatAgent(agent: string): string {
    if (!agent) return '';
    if (agent.includes('/')) return agent.split('/')[1];
    if (agent.includes('\\')) return agent.split('\\')[1];
    return agent.split('@')[0];
  }
}
</script>

<style scoped>
.patient-call-log {
  container-type: inline-size;
}

.filter-grid {
  display: grid;
  gap: 1rem;
}

.call-log-stacked {
  display: none;
}

.call-card {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.call-card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  overflow-wrap: anywhere;
}

.call-card-row > * {
  min-width: 0;
}

.call-card-row + .call-card-row {
  margin-top: 0.125rem;
}

.call-card-label {
  font-weight: 600;
}

.call-log-table ::v-deep(td.call-log-agent-cell) {
  overflow-wrap: anywhere;
}

@container (max-width: 450px) {
  .call-log-table {
    display: none;
  }

  .call-log-stacked {
    display: block;
  }

  .filter-grid ::v-deep(.d-flex) {
    flex-wrap: wrap;
  }
}
</style>
